import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChevronUp } from '@/components/Icons'
import {
  StyledGroupHeader,
  StyledList,
  StyledListItemIcon,
  StyledListItemText,
} from '@/components/Sidebar/Sidebar.styled'
import SidebarItem from '@/components/Sidebar/SidebarItem/SidebarItem'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.tsx'
import { SidebarGroupConfig } from '@/features/sidebar/types'
import { generateNavigationId } from '@/features/sidebar/utils/sidebarHelpers'
import { cn } from '@/utils'

// Constants
const ICON_SIZE = 20
const ICON_COLOR = 'var(--Colors-Foreground-fg-quaternary-400)'

export interface SidebarGroupProps {
  config: SidebarGroupConfig
  isCollapsed?: boolean
}

const SidebarGroup: React.FC<SidebarGroupProps> = ({
  config,
  isCollapsed = false,
}) => {
  const { t } = useTranslation()

  const [isExpanded, setIsExpanded] = useState(true)

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const groupId = generateNavigationId(config.label)
  const IconComponent = config.icon

  // When collapsed, render only the icon with tooltip
  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <StyledGroupHeader
            id={groupId}
            aria-label={t(config.label)}
            className="justify-center !p-2"
          >
            {IconComponent && (
              <StyledListItemIcon>
                <IconComponent size={ICON_SIZE} color={ICON_COLOR} />
              </StyledListItemIcon>
            )}
          </StyledGroupHeader>
        </TooltipTrigger>
        <TooltipContent side="right">
          <span>{t(config.label)}</span>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div aria-labelledby={groupId} className="m-0">
      {/* Group Header */}
      <Collapsible open={isExpanded}>
        <CollapsibleTrigger
          asChild
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
        >
          <StyledGroupHeader
            id={groupId}
            aria-expanded={isExpanded}
            onClick={handleToggle}
          >
            {IconComponent && (
              <StyledListItemIcon>
                <IconComponent size={ICON_SIZE} color={ICON_COLOR} />
              </StyledListItemIcon>
            )}

            <StyledListItemText>{t(config.label)}</StyledListItemText>
            <ChevronUp
              className={cn(
                'ml-auto transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          </StyledGroupHeader>
        </CollapsibleTrigger>

        {/* Use Accordion/Collapsible component here*/}
        <CollapsibleContent
          className={cn(
            'overflow-hidden transition-all duration-300 ease-in-out',
            isExpanded ? 'animate-collapsible-down' : 'animate-collapsible-up'
          )}
        >
          <div className="min-h-0">
            <StyledList>
              {config.children.map((child) => (
                <SidebarItem
                  key={child.id}
                  config={{
                    ...child,
                    icon: undefined,
                  }}
                  isCollapsed={isCollapsed}
                />
              ))}
            </StyledList>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

SidebarGroup.displayName = 'SidebarGroup'

export default React.memo(SidebarGroup, (prevProps, nextProps) => {
  return (
    prevProps.config === nextProps.config &&
    prevProps.isCollapsed === nextProps.isCollapsed
  )
})
